// Composable inline entity inspector — production port from
// `storybook/src/prototypes/modern-design/components/EntityInspector.tsx`.
// Used inside `DocumentDetailOverlay` when the analyst expands a specific
// entity row in the accordion.

import { Check, Loader2, Pencil, PlusCircle, Sparkles, Trash2, X } from 'lucide-react'
import type React from 'react'
import { Button } from '@/components/ui/button'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ScoreFactor = {
  label: string
  /** 0–1 fraction. */
  value: number
}

export type FullEntityDetail = {
  /** 0–1 — rendered as the green "Match" badge. */
  matchConfidence: number | null
  /** 0–100 investigation-importance score. Null when not yet computed. */
  investigationScore: number | null
  /** Description shown under the investigation-score header. */
  investigationDescription?: string
  scoreFactors: ScoreFactor[]
  /** Number of relationships this entity participates in. */
  connections: number
  /** Distinct source-document count. */
  sources: number
  /** Whether the entity is currently flagged as a suspect — drives the
   *  "Mark as suspect" action's toggle state in the document overlay. */
  flaggedAsSuspect: boolean
  /** Free-form key/value records — full property bag. */
  properties: { key: string; value: string }[]
}

// ─── Match badge ─────────────────────────────────────────────────────────────

export function MatchBadge({ confidence }: { confidence: number | null }) {
  if (confidence == null) return null
  const pct = Math.round(confidence * 100)
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 9px',
        borderRadius: '9999px',
        background: 'var(--success-surface)',
        color: 'var(--success)',
        border: '1px solid var(--success)',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'var(--font-mono)',
        letterSpacing: 0.2,
      }}
    >
      Match: {pct}%
    </span>
  )
}

// ─── Investigation score card ───────────────────────────────────────────────

export function InvestigationScoreCard({ detail }: { detail: FullEntityDetail }) {
  const score = detail.investigationScore == null ? null : Math.round(detail.investigationScore)
  return (
    <div
      style={{
        position: 'relative',
        padding: '14px 14px',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        background: 'var(--card)',
        boxShadow: 'inset 0 0 0 1px rgba(199, 127, 42, 0.10)',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 2,
          background:
            'linear-gradient(90deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 60%, transparent) 60%, transparent 100%)',
          opacity: 0.7,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 14,
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Sparkles size={12} style={{ color: 'var(--primary)' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)' }}>
              Investigation score
            </span>
          </div>
          {detail.investigationDescription && (
            <p
              style={{
                margin: 0,
                fontSize: 11.5,
                lineHeight: 1.55,
                color: 'var(--foreground)',
              }}
            >
              {detail.investigationDescription}
            </p>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 26,
              fontWeight: 500,
              letterSpacing: -1,
              color: 'var(--primary)',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {score == null ? '—' : score}
          </span>
          <span
            style={{
              fontSize: 9,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              fontWeight: 600,
            }}
          >
            / 100
          </span>
        </div>
      </div>
      <ScoreBar value={(detail.investigationScore ?? 0) / 100} prominent />
      {detail.scoreFactors.length > 0 && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
          }}
        >
          {detail.scoreFactors.map(factor => (
            <ScoreFactorRow key={factor.label} factor={factor} />
          ))}
        </div>
      )}
    </div>
  )
}

function ScoreBar({ value, prominent = false }: { value: number; prominent?: boolean }) {
  const v = Math.max(0, Math.min(1, value))
  return (
    <div
      style={{
        position: 'relative',
        height: prominent ? 6 : 5,
        borderRadius: 999,
        background: 'var(--card)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          width: `${v * 100}%`,
          background: prominent
            ? 'linear-gradient(90deg, var(--primary) 0%, var(--primary) 100%)'
            : 'var(--primary)',
          borderRadius: 999,
          transition: 'width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      />
    </div>
  )
}

function ScoreFactorRow({ factor }: { factor: ScoreFactor }) {
  const pct = Math.round(factor.value * 100)
  return (
    <div
      // Phone: a shorter bar, so the factor's name keeps most of the row.
      className="grid grid-cols-[minmax(0,1fr)_88px_40px] md:grid-cols-[1fr_160px_40px]"
      style={{
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--foreground)',
          letterSpacing: 0.1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {factor.label}
      </span>
      <ScoreBar value={factor.value} />
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--foreground)',
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
          fontWeight: 500,
        }}
      >
        {pct}%
      </span>
    </div>
  )
}

// ─── Stat triplet ────────────────────────────────────────────────────────────

export function EntityStatTriplet({ detail }: { detail: FullEntityDetail }) {
  const scoreVal = detail.investigationScore == null ? '—' : Math.round(detail.investigationScore)
  const items = [
    { label: 'Connections', value: String(detail.connections), accent: false },
    { label: 'Sources', value: String(detail.sources), accent: false },
    { label: 'Score', value: String(scoreVal), accent: true },
  ]
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 0,
        borderRadius: '12px',
        border: '1px solid var(--border)',
        background: 'var(--card)',
        overflow: 'hidden',
      }}
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          style={{
            padding: '14px 12px 14px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            borderLeft: i > 0 ? '1px solid var(--border)' : 'none',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: -0.8,
              lineHeight: 1,
              color: item.accent ? 'var(--primary)' : 'var(--foreground)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {item.value}
          </span>
          <span
            style={{
              fontSize: 9.5,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              color: 'var(--muted-foreground)',
              fontWeight: 600,
            }}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── Action row ─────────────────────────────────────────────────────────────

export function EntityActionRow({
  onAddToGraph,
  onEdit,
  onDelete,
  isDeleting = false,
  osintAction,
}: {
  onAddToGraph?: () => void
  onEdit?: () => void
  onDelete?: () => void
  isDeleting?: boolean
  /** Connected "run OSINT on this entity" action, supplied by the row container.
   *  A slot rather than a callback: the local ActionButton has no i18n and no
   *  pending state, and this keeps both out of it. */
  osintAction?: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <ActionButton icon={<PlusCircle size={12} />} label="Add to graph" onClick={onAddToGraph} />
      {osintAction}
      {onEdit && <ActionButton icon={<Pencil size={12} />} label="Edit" onClick={onEdit} />}
      {onDelete && (
        <ActionButton
          icon={isDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          label={isDeleting ? 'Deleting…' : 'Delete'}
          onClick={onDelete}
          tone="destructive"
          disabled={isDeleting}
        />
      )}
    </div>
  )
}

const TONE_VARIANT = {
  default: 'outline',
  primary: 'default',
  destructive: 'destructive',
} as const

function ActionButton({
  icon,
  label,
  onClick,
  tone = 'default',
  disabled = false,
}: {
  icon: React.ReactNode
  label: string
  onClick?: () => void
  tone?: keyof typeof TONE_VARIANT
  disabled?: boolean
}) {
  return (
    <Button
      type="button"
      variant={TONE_VARIANT[tone]}
      size="xs"
      className="max-md:h-9"
      onClick={onClick}
      disabled={disabled || !onClick}
    >
      {icon}
      {label}
    </Button>
  )
}

// ─── Editable properties list ──────────────────────────────────────────────

export function EditablePropertiesList({
  properties,
  onChange,
}: {
  properties: { key: string; value: string }[]
  onChange: (key: string, value: string) => void
}) {
  return (
    <div
      style={{
        borderRadius: '12px',
        border: '1px solid var(--primary)',
        background: 'var(--card)',
        overflow: 'hidden',
        boxShadow: 'inset 0 0 0 1px rgba(199, 127, 42, 0.10)',
      }}
    >
      <PropertyListHeader
        icon={<Pencil size={12} />}
        label="Edit properties"
        count={properties.length}
      />
      <div>
        {properties.map((prop, i) => (
          <EditablePropertyRow
            key={prop.key}
            propertyKey={prop.key}
            value={prop.value}
            isLast={i === properties.length - 1}
            onChange={next => onChange(prop.key, next)}
          />
        ))}
      </div>
    </div>
  )
}

// Key / value columns of a property row. On a phone the fixed 160px key column
// would leave the value a sliver, so the two share the row 2 : 3 instead.
const PROPERTY_ROW_GRID =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:grid-cols-[minmax(110px,160px)_1fr]'

// An editable row stacks the label above its field on a phone: beside the label
// a 16px input shows about fifteen characters. Key / value columns from `md` up.
const EDITABLE_PROPERTY_ROW_GRID =
  'grid grid-cols-1 gap-1.5 md:grid-cols-[minmax(110px,160px)_1fr] md:gap-3'

// 16px on a phone, so iOS does not zoom the page when a field takes focus; the
// dense 11.5px from `md` up.
const PROPERTY_INPUT_TEXT = 'text-base md:text-[11.5px]'

function PropertyListHeader({
  icon,
  label,
  count,
}: {
  icon: React.ReactNode
  label: string
  count: number
}) {
  return (
    <div
      style={{
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderBottom: '1px solid var(--border)',
        background: 'var(--card)',
        color: 'var(--primary)',
      }}
    >
      {icon}
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)' }}>{label}</span>
      <span
        style={{
          fontSize: 10.5,
          color: 'var(--muted-foreground)',
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count}
      </span>
    </div>
  )
}

function EditablePropertyRow({
  propertyKey,
  value,
  isLast,
  onChange,
}: {
  propertyKey: string
  value: string
  isLast: boolean
  onChange: (next: string) => void
}) {
  const isMultiline = value.length > 80 || value.includes('\n')
  const inputStyle: React.CSSProperties = {
    width: '100%',
    fontFamily: 'var(--font-mono)',
    color: 'var(--foreground)',
    letterSpacing: 0.1,
    lineHeight: 1.5,
    background: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    padding: '6px 8px',
    outline: 'none',
    boxSizing: 'border-box',
  }
  return (
    <div
      className={EDITABLE_PROPERTY_ROW_GRID}
      style={{
        padding: '9px 14px',
        alignItems: isMultiline ? 'start' : 'center',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
      }}
    >
      <label
        htmlFor={`prop-edit-${propertyKey}`}
        // The top padding lines the label up with a textarea's first line, which
        // only matters while the two sit side by side.
        className={isMultiline ? 'md:pt-1.5' : undefined}
        style={{
          fontSize: 11.5,
          color: 'var(--muted-foreground)',
          fontWeight: 500,
          letterSpacing: 0.1,
        }}
      >
        {prettifyKey(propertyKey)}
      </label>
      {isMultiline ? (
        <textarea
          id={`prop-edit-${propertyKey}`}
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={Math.min(8, Math.max(2, value.split('\n').length))}
          // Phone: four lines to start with — a value long enough for a textarea
          // fills at least three there, and the resize grip is hard to drag by touch.
          className={`${PROPERTY_INPUT_TEXT} min-h-30 md:min-h-[60px]`}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          id={`prop-edit-${propertyKey}`}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={PROPERTY_INPUT_TEXT}
          style={inputStyle}
        />
      )}
    </div>
  )
}

// ─── Save bar ───────────────────────────────────────────────────────────────

export function EntitySaveBar({
  onCancel,
  onSave,
  isSaving,
}: {
  onCancel: () => void
  onSave: () => void
  isSaving: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        paddingTop: 4,
      }}
    >
      <ActionButton
        icon={<X size={12} />}
        label="Cancel"
        onClick={isSaving ? undefined : onCancel}
        disabled={isSaving}
      />
      <ActionButton
        icon={isSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        label={isSaving ? 'Saving…' : 'Save'}
        onClick={onSave}
        tone="primary"
        disabled={isSaving}
      />
    </div>
  )
}

// ─── Properties list ────────────────────────────────────────────────────────

export function EntityPropertiesList({
  properties,
}: {
  properties: { key: string; value: string }[]
}) {
  return (
    <div
      style={{
        borderRadius: '12px',
        border: '1px solid var(--border)',
        background: 'var(--card)',
        overflow: 'hidden',
      }}
    >
      <PropertyListHeader
        icon={<Sparkles size={12} />}
        label="Properties"
        count={properties.length}
      />
      <div>
        {properties.map((prop, i) => (
          <PropertyRow
            key={prop.key}
            label={prop.key}
            value={prop.value}
            isLast={i === properties.length - 1}
          />
        ))}
      </div>
    </div>
  )
}

function PropertyRow({ label, value, isLast }: { label: string; value: string; isLast: boolean }) {
  return (
    <div
      className={PROPERTY_ROW_GRID}
      style={{
        gap: 12,
        padding: '9px 14px',
        alignItems: 'baseline',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
      }}
    >
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--muted-foreground)',
          fontWeight: 500,
          letterSpacing: 0.1,
        }}
      >
        {prettifyKey(label)}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11.5,
          color: 'var(--foreground)',
          letterSpacing: 0.1,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  )
}

// Convert raw API keys (`source_filename`, `cell_id`) into human labels.
export function prettifyKey(raw: string): string {
  if (!raw) return raw
  const trimmed = raw.replace(/^_+/, '')
  return trimmed
    .split('_')
    .filter(Boolean)
    .map((part, i) => (i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}
